import { Link } from 'react-router-dom';
import ScrollSceneSection, { ModelCredit, type SceneStage } from './ScrollSceneSection';
import type { ExplodedScene } from './explodedScene';

/**
 * Homepage section: a gaming PC that comes apart part by part as you scroll,
 * and goes back together when you scroll up (owner's request, 2026-10-09;
 * colour + RGB pass 2026-10-10). Layout and loading: ScrollSceneSection /
 * useScrollScene; the 3D side: explodedScene.ts.
 */
const STAGES: SceneStage[] = [
  { at: 0, title: 'מה יש בתוך מחשב גיימינג?', text: 'גללו למטה, ונפרק אותו רכיב אחרי רכיב.' },
  {
    at: 0.08, focus: 'case', parts: ['case'], tag: 'המארז', photo: 'part-case', photoAlt: 'מארז מחשב פתוח עם דופן זכוכית',
    title: 'פותחים את המארז', text: 'כל תיקון מתחיל במבט פנימה. קודם מבינים מה קרה, ורק אז מחליפים משהו.',
  },
  {
    at: 0.2, focus: 'gpu', parts: ['gpu'], tag: 'כרטיס מסך', photo: 'part-gpu', photoAlt: 'כרטיס מסך עם שני מאווררים', title: 'כרטיס מסך',
    text: 'הרכיב היקר ביותר במחשב. ניקוי אבק, החלפת משחה טרמית ובדיקת חום, לפני שקונים חדש.',
    link: { to: '/services', label: 'שירותי המעבדה' },
  },
  {
    at: 0.34, focus: 'pump', parts: ['pump', 'waterblock', 'radiator', 'fan0', 'fan1', 'fan2'], tag: 'קירור נוזלי',
    photo: 'part-cooling', photoAlt: 'משאבה עם נוזל קירור כחול, ראש קירור למעבד ורדיאטור עם שלושה מאווררים', title: 'קירור',
    text: 'מחשב רועש, חם, או שנכבה לבד? ברוב המקרים זה הקירור: מאווררים, משאבה ורדיאטור.',
    link: { to: '/diagnose', label: 'ייעוץ חינם' },
  },
  {
    at: 0.5, focus: 'ram1', parts: ['cpu', 'ram0', 'ram1', 'ram2', 'ram3'], tag: 'זיכרון ומעבד',
    photo: 'part-cpu-ram', photoAlt: 'מעבד ושני כרטיסי זיכרון עם תאורה', title: 'מעבד וזיכרון',
    text: 'שדרוג זיכרון הוא השיפור הזול ביותר למחשב שנתקע בין חלונות.',
    link: { to: '/blog/kama-ram-zikaron-machshev-nayad-2026', label: 'כמה זיכרון באמת צריך?' },
  },
  {
    at: 0.64, focus: 'ssd', parts: ['ssd', 'm2'], tag: 'כוננים', photo: 'part-storage', photoAlt: 'כונן M.2 וכונן SSD', title: 'אחסון',
    text: 'מעבר לכונן SSD מחזיר חיים למחשב איטי, וכל הקבצים נשארים במקום.',
    link: { to: '/slow-computer', label: 'מחשב איטי? מה עושים' },
  },
  {
    at: 0.76, focus: 'psu', parts: ['psu', 'motherboard'], tag: 'ספק כוח', photo: 'part-psu-board', photoAlt: 'ספק כוח עם כבלים ולוח אם', title: 'ספק כוח ולוח אם',
    text: 'כשהמחשב לא נדלק בכלל, כאן מתחילים לחפש.',
    link: { to: '/diagnose', label: 'ייעוץ חינם' },
  },
  { at: 0.88, title: 'מכירים כל רכיב מקרוב', text: 'תיקון, שדרוג, או מחשב חדש שמרכיבים בשבילכם לפי מה שאתם צריכים.' },
];

const credit = (
  <ModelCredit author="Daniel Cardona" url="https://sketchfab.com/3d-models/dream-computer-setup-82f78bbaf2d34f01af854a52151dbf49" />
);

export default function ExplodedPC() {
  return (
    <ScrollSceneSection<ExplodedScene>
      id="exploded-pc"
      stages={STAGES}
      poster="/models/pc-assembled-poster.webp"
      heightClass="h-[340vh] md:h-[420vh]"
      eager
      create={async (host, onFrame) => (await import('./explodedScene')).createExplodedScene(host, { onFrame })}
      finalCta={
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/diagnose" className="cin-btn-primary !py-3 !px-6">ייעוץ חינם</Link>
          <Link to="/build-pc" className="cin-btn-ghost cin-btn-on-dark !py-3 !px-6">הרכבת מחשב</Link>
        </div>
      }
      credit={credit}
      staticBody={
        <>
          <h2 className="cin-display !text-[clamp(2rem,4vw,3.25rem)] mb-4 text-white">{STAGES[0].title}</h2>
          <p className="max-w-2xl text-white/70 text-lg mb-10">כל רכיב במחשב, ומה אפשר לעשות כשהוא עושה בעיות.</p>
          <img
            src="/models/pc-exploded-poster.webp"
            alt="מחשב גיימינג מפורק לרכיבים: מארז, לוח אם, מעבד, זיכרון, כרטיס מסך, קירור נוזלי וספק כוח"
            width={1600}
            height={1000}
            loading="lazy"
            decoding="async"
            className="w-full h-auto rounded-2xl mb-12"
          />
          <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {STAGES.slice(2, 7).map((s) => (
              <li key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-white/70 leading-relaxed mb-3">{s.text}</p>
                {s.link && <Link to={s.link.to} className="text-[#8FB3FF] font-semibold hover:underline">{s.link.label} ←</Link>}
              </li>
            ))}
          </ol>
        </>
      }
    />
  );
}
